import { isTiptapTokenCloseReason } from '@/lib/tiptap-collab-auth';
import authService from '@/services/zybook-auth-service';
import { TiptapCollabProvider } from '@tiptap-pro/provider';
import { useEffect, useMemo, useState } from 'react';
import * as Y from 'yjs';

type UseTiptapCollaborationProviderResult = {
    ydoc: Y.Doc;
    collabProvider: TiptapCollabProvider | null;
    innerEditorKey: number;
    isSynced: boolean;
    isReauthenticating: boolean;
};

export function useTiptapCollaborationProvider(documentName: string | undefined): UseTiptapCollaborationProviderResult {
    const ydoc = useMemo(() => new Y.Doc(), []);
    const [syncedDocumentName, setSyncedDocumentName] = useState<string | null>(null);
    const [collabProvider, setCollabProvider] = useState<TiptapCollabProvider | null>(null);
    const [innerEditorKey, setInnerEditorKey] = useState(0);
    const [isReauthenticating, setIsReauthenticating] = useState(false);

    const isSynced = syncedDocumentName === documentName;

    useEffect(() => {
        if (!documentName) {
            throw new Error('Tiptap document name is required for collaboration');
        }

        const userId = authService.getUserId();
        if (!userId) {
            throw new Error('User ID is required for collaboration');
        }

        let cancelled = false;
        let provider: TiptapCollabProvider | null = null;
        let reconnecting = false;

        const handleProviderSynced = (newProvider: TiptapCollabProvider, isReconnect: boolean) => {
            if (cancelled) {
                return;
            }

            if (!ydoc.getMap('config').get('initialContentLoaded')) {
                ydoc.getMap('config').set('initialContentLoaded', true);
            }

            setSyncedDocumentName(documentName);
            setCollabProvider(newProvider);

            if (isReconnect) {
                setInnerEditorKey(key => key + 1);
                setIsReauthenticating(false);
            }
        };

        const createProvider = (token: string, isReconnect: boolean): TiptapCollabProvider | null => {
            const newProvider = new TiptapCollabProvider({
                name: documentName,
                appId: import.meta.env.VITE_TIPTAP_APP_ID,
                token,
                document: ydoc,
                user: userId,
                onSynced() {
                    handleProviderSynced(newProvider, isReconnect);
                },
                onClose({ event }) {
                    if (cancelled || reconnecting || !isTiptapTokenCloseReason(event.reason)) {
                        return;
                    }

                    reconnecting = true;
                    setIsReauthenticating(true);
                    void (async () => {
                        try {
                            newProvider.destroy();

                            if (cancelled) {
                                return;
                            }

                            const freshToken = await authService.getTiptapToken(true);
                            if (cancelled || !freshToken) {
                                throw new Error('Failed to refresh TipTap token after connection close');
                            }

                            const reconnectedProvider = createProvider(freshToken, true);
                            if (cancelled) {
                                reconnectedProvider?.destroy();
                                return;
                            }

                            provider = reconnectedProvider;
                        } finally {
                            reconnecting = false;
                        }
                    })();
                },
            });

            if (cancelled) {
                newProvider.destroy();
                return null;
            }

            return newProvider;
        };

        void (async () => {
            const freshToken = await authService.getTiptapToken(true);
            if (cancelled) {
                return;
            }

            if (!freshToken) {
                console.error('TipTap token is required for collaboration');
                return;
            }

            provider = createProvider(freshToken, false);
        })();

        return () => {
            cancelled = true;
            provider?.destroy();
        };
    }, [documentName, ydoc]);

    return {
        ydoc,
        collabProvider,
        innerEditorKey,
        isSynced,
        isReauthenticating,
    };
}
